import type { SurfaceToken } from "../../components/ui/box-classes";
import type { IconSource } from "../../components/ui/icon";
import type { Color } from "../../lib/colors";
import * as React from "react";
import type { Omittable } from "../../lib/props";

export type EntityAvatarConfig = Omittable<{
  src?: string;
  fallback?: string;
  icon?: IconSource;
  /** Color name from the shared scale. Narrow stored strings with `toColor`. */
  color?: Color;
  /** When true, renders the icon in a padded rounded box instead of inside an avatar circle. */
  padded?: boolean;
  /** Custom render function for the avatar. When provided, overrides all other avatar properties. */
  render?: () => React.ReactNode;
}>;

export type EntityItemConfig = Omittable<{
  id: string;
  title: string;
  description?: string;
  /** Section this item sits under. A run of consecutive items sharing a
   *  section renders one eyebrow heading above it, and a pool whose items
   *  carry sections suppresses its own pool-label header (the sections ARE
   *  the headers). Search filters items as usual — a section with no matches
   *  disappears with them. */
  section?: string;
  /** Optional SECOND grouping level nested under `section` — a run of
   *  consecutive items sharing a (section, subsection) pair renders an indented
   *  sub-eyebrow above it. With `collapsibleSections` each level is
   *  independently collapsible: closing the section hides its subsections and
   *  their items; closing a subsection hides only its own items.
   *  Requires `section`; ignored without one. */
  subsection?: string;
  /** Hover tooltip on the item row — explanation text that shouldn't occupy
   *  an inline description line (e.g. dynamic-reference entries). */
  tooltip?: string;
  avatar?: EntityAvatarConfig;
  keywords?: string[];
  disabled?: boolean;
  muted?: boolean;
  invited?: boolean;
  data?: Record<string, unknown>;
}>;

export type EntityBadgeConfig = {
  label: string;
};

export type EntityPoolConfig = Omittable<{
  id: string;
  label: string;
  icon?: IconSource;
  items: EntityItemConfig[];
  /** Items rendered ABOVE the pool's section header, inside this pool's tab —
   *  outside virtual-scroll accounting (`total`/`onLoadRange` ignore them).
   *  Selectable like regular items; filtered by the same search matcher.
   *  E.g. dynamic user references pinned above a members list. */
  pinnedItems?: EntityItemConfig[];
  itemBadge?: EntityBadgeConfig;
  /** Hide avatar/icon column for all items in this pool. @default false */
  hideAvatars?: boolean;
  /** Total item count for virtual scrolling. When set, the list renders at full height with placeholders for unloaded items. */
  total?: number;
  /** Called when the visible range changes, for loading items on demand. Requires `total` to be set. */
  onLoadRange?: (startIndex: number, endIndex: number) => void;
  /** Called when the search query changes, for loading matching items on demand from the server. */
  onSearchChange?: (query: string) => void;
  /**
   * Skip the client-side `filterEntityItems` pass and render `items` as-is. Set
   * on pools that already filter server-side via `onSearchChange` to avoid
   * double-filtering. @default false
   */
  serverFiltered?: boolean;
  /**
   * Make each `section` (and `subsection`) eyebrow a pressable disclosure that
   * collapses its run of items. For a list subdivided into many sections (e.g.
   * one per integration provider) where a user works in one at a time.
   *
   * A folded section's items stay in the filtered set but leave the KEYBOARD
   * ORDER: a row with no DOM cannot be highlighted or scrolled to, so letting
   * the cursor reach it meant ArrowDown walked into nothing and Enter committed
   * a row the user never saw.
   *
   * Browse and search hold SEPARATE collapse state. A search starts fully
   * expanded (it never inherits what was collapsed while browsing, or matches
   * would hide behind a closed heading and read as "no results"), but the
   * headings stay interactive so a broad result set can still be narrowed. The
   * search-time state is discarded when the query clears.
   * @default false
   */
  collapsibleSections?: boolean;
  /**
   * Sections that start folded (by label), when `collapsibleSections`. Anything
   * omitted starts expanded. Top-level `section` labels only — subsections
   * always start expanded.
   *
   * Applied per LABEL, exactly once each, so a section whose label only appears
   * once an async query resolves still opens folded, while a section the user has
   * since expanded is never re-folded by a later render of the same list.
   */
  defaultCollapsedSections?: string[];
  /**
   * True while `items` is still being fetched. Suppresses the empty state — an
   * unfetched pool is indistinguishable from an empty one by item count alone,
   * and "nothing to select" is a claim about the data, not about the request.
   * @default false
   */
  loading?: boolean;
}>;

export type EntitySelectorConfig = Omittable<{
  pools: EntityPoolConfig[];
  defaultPool?: string;
  searchPlaceholder?: string;
  /**
   * Copy for the SEARCHED-empty state: the pool has items, the query matched
   * none of them. @default "No results found"
   */
  emptyMessage?: string;
  /**
   * Copy for the UNSEARCHED-empty state: the pool has nothing to offer at all,
   * so there is no query to relax and "no results" would be a lie. Falls back
   * to `emptyMessage`. @default "Nothing to select"
   */
  noItemsMessage?: string;
  maxSelections?: number;
  /**
   * Stack each item's description on its own line(s) beneath the title instead
   * of inline beside it, letting longer descriptions wrap. @default false
   */
  multilineItems?: boolean;
  /**
   * The surface the list paints on, so item hover and selection wash
   * against the right substrate. The `popover` and `drawer` variants are
   * always on that surface; an `inline` selector names whatever its host paints.
   * @default "popover"
   */
  surface?: SurfaceToken;
}>;

export type EntitySelection = {
  id: string;
  poolId: string;
  entity: EntityItemConfig;
};

/** Presentation is explicit per call site: `popover` (default) anchors a
 *  Popover to the trigger, `drawer` presents a bottom drawer, `inline` renders
 *  the list with no overlay at all. There is no automatic mobile swap. */
export type EntitySelectorVariant = "popover" | "inline" | "drawer";

export type EntitySelectorContextValue = {
  config: EntitySelectorConfig;
  variant: EntitySelectorVariant;
  selections: EntitySelection[];
  toggleSelection: (entity: EntityItemConfig, poolId: string) => void;
  isSelected: (entityId: string) => boolean;
  clearSelections: () => void;
  activePool: string;
  setActivePool: (poolId: string) => void;
  search: string;
  setSearch: (search: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  getPool: (poolId: string) => EntityPoolConfig | undefined;
  getPoolBadge: (poolId: string) => EntityBadgeConfig | undefined;
  getFilteredItems: (poolId: string) => EntityItemConfig[];
  /**
   * The pool's pinned rows, search-filtered. Computed in the root (not the
   * pool renderer) because pinned rows are part of the keyboard-nav list —
   * they render first, so they occupy nav indices 0..n-1 ahead of
   * `getFilteredItems`.
   */
  getPinnedItems: (poolId: string) => EntityItemConfig[];
  /**
   * Whether `key` — a section label, or `subsectionKey(section, subsection)` —
   * is folded in `poolId`. Collapse state lives in the ROOT for the same reason
   * pinned rows do: a folded section's rows are not on screen, so the root drops
   * them from the keyboard-nav list too. Held in the pool renderer, the cursor
   * could walk onto a row that produced no DOM and Enter would commit it.
   *
   * Browse and search keep SEPARATE sets. A search must never inherit what was
   * folded while browsing — matches would sit hidden behind a closed heading,
   * reading as "no results" — so search starts fully expanded, and its set is
   * discarded when the query clears so it cannot leak back into browse state.
   */
  isSectionCollapsed: (poolId: string, key: string) => boolean;
  toggleSection: (poolId: string, key: string) => void;
  activeIndex: number;
  activeId: string | null;
  /** The POINTER moved onto row `index`. Hover is the only caller — keyboard
   *  navigation goes through `moveActive`, and the two are kept apart so the
   *  list can scroll for one and hold still for the other. */
  setActiveIndex: (index: number) => void;
  /**
   * Whether the active row was last set by the pointer rather than the keyboard.
   *
   * Read it before scrolling the active row into view. Keyboard navigation MUST
   * scroll — the next row can be off-screen. Hover must NOT: the row under the
   * cursor is by definition already visible, so "scrolling it into view" only
   * slides the list, which puts a different row under a stationary cursor and
   * can cascade into a second scroll. A partially clipped row at the edge of the
   * list is where it bites, because that is the row a user aims at first.
   */
  isActiveFromPointer: () => boolean;
  moveActive: (delta: number) => void;
  selectActive: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  focusInput: () => void;
};

// Composite key for a subsection. NUL cannot occur in a real label, so it cannot
// collide with a top-level section's key. It is spelled as an ESCAPE and never as
// a literal byte: a literal NUL makes git treat this whole source file as binary,
// which costs every future three-way merge on it.
export function subsectionKey(section: string, subsection: string): string {
  return `${section}\u0000${subsection}`;
}

// Whether `item` sits under a folded heading — its own section, or its
// subsection within an open section. Shared by the root (which drops such rows
// from the keyboard-nav list) and the pool (which does not render them), so the
// two can never disagree about which rows are on screen.
export function isItemCollapsed(
  item: EntityItemConfig | null | undefined,
  isCollapsed: (key: string) => boolean,
): boolean {
  if (!item?.section) return false;
  if (isCollapsed(item.section)) return true;
  return (
    !!item.subsection && isCollapsed(subsectionKey(item.section, item.subsection))
  );
}

export function filterEntityItems(
  items: EntityItemConfig[],
  search: string,
): EntityItemConfig[] {
  const valid = items.filter((item) => item?.id && item?.title);
  if (!search.trim()) return valid;

  const query = search.toLowerCase().trim();
  return valid.filter(
    (item) =>
      item.title.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.keywords?.some((kw) => kw.toLowerCase().includes(query)) ||
      item.id.toLowerCase().includes(query),
  );
}

const EntitySelectorContext =
  React.createContext<EntitySelectorContextValue | null>(null);
EntitySelectorContext.displayName = "EntitySelectorContext";

export function useEntitySelector(): EntitySelectorContextValue {
  const context = React.useContext(EntitySelectorContext);
  if (!context) {
    throw new Error(
      "useEntitySelector must be used within an EntitySelector component.",
    );
  }
  return context;
}

export function useEntitySelectorSelected(entityId: string): boolean {
  const context = useEntitySelector();
  return context.isSelected(entityId);
}

export function useEntitySelectorSelections(): EntitySelection[] {
  const context = useEntitySelector();
  return context.selections;
}

export function useEntitySelectorActivePool(): string {
  const context = useEntitySelector();
  return context.activePool;
}

export function useEntitySelectorSearch(): string {
  const context = useEntitySelector();
  return context.search;
}

export function useEntitySelectorOpen(): boolean {
  const context = useEntitySelector();
  return context.open;
}

type EntitySelectorProviderProps = {
  value: EntitySelectorContextValue;
  children: React.ReactNode;
};

function EntitySelectorProvider({
  value,
  children,
}: EntitySelectorProviderProps) {
  return (
    <EntitySelectorContext.Provider value={value}>
      {children}
    </EntitySelectorContext.Provider>
  );
}

export { EntitySelectorContext, EntitySelectorProvider };
export type { EntitySelectorProviderProps };
